// Service pour gérer les statistiques des providers
interface ProviderStat {
  providerId: string;
  name: string;
  size: number;
  timestamp: number;
}

// Clé utilisée pour stocker les données dans le localStorage
const PROVIDER_STATS_KEY = 'flashback_provider_stats';

/**
 * Récupère les statistiques des providers depuis le localStorage
 */
export const getProviderStats = (): ProviderStat[] => {
  try {
    const stats = localStorage.getItem(PROVIDER_STATS_KEY);
    return stats ? JSON.parse(stats) : [];
  } catch (error) {
    return [];
  }
};

/**
 * Ajoute un nouveau provider aux statistiques
 * @param providerId ID du provider
 * @param name Nom du provider
 * @param size Taille en octets
 */
export const addProviderStat = (
  providerId: string,
  name: string,
  size: number = 1024 * 1024 * 1024
): void => {
  try {
    const stats = getProviderStats();

    // Ajouter la nouvelle statistique
    stats.push({
      providerId,
      name,
      size,
      timestamp: Date.now(),
    });

    // Sauvegarder dans le localStorage
    localStorage.setItem(PROVIDER_STATS_KEY, JSON.stringify(stats));
  } catch (error) { /* Ignore errors */ }
};

/**
 * Récupère les données agrégées pour le graphique de répartition des providers
 * @returns Données formatées pour le graphique
 */
export const getProviderChartData = () => {
  const stats = getProviderStats();

  if (stats.length === 0) {
    // Données par défaut si aucune statistique n'est disponible
    return [
      { name: 'Google', value: 350, color: '#788CFF' },
      { name: 'AWS', value: 250, color: '#8e9dff' },
      { name: 'StorJ', value: 250, color: '#EBBDA3' },
      { name: 'Other', value: 150, color: '#f0c7b1' },
    ];
  }

  // Agréger les données par provider
  const providerMap: Record<string, { name: string; value: number; color: string }> = {};

  // Couleurs pour les providers connus
  const providerColors: Record<string, string> = {
    'google-cloud': '#788CFF',
    aws: '#8e9dff',
    storej: '#EBBDA3',
    azure: '#6B9AE5',
    'alibaba-cloud': '#FF7F50',
    filecoin: '#90EE90',
    'impossible-cloud': '#FFD700',
    ovhcloud: '#FF69B4',
    walrus: '#A8DADC',
    other: '#f0c7b1',
  };

  // Calculer la valeur totale pour chaque provider
  stats.forEach((stat) => {
    const providerName = stat.name;
    const providerId = stat.providerId;

    if (!providerMap[providerId]) {
      providerMap[providerId] = {
        name: providerName,
        value: 0,
        color: providerColors[providerId] || '#CCCCCC',
      };
    }

    providerMap[providerId].value += stat.size;
  });

  // Convertir l'objet en tableau pour le graphique
  return Object.values(providerMap);
};

/**
 * Réinitialise toutes les statistiques des providers
 */
export const resetProviderStats = (): void => {
  localStorage.removeItem(PROVIDER_STATS_KEY);
};
